import type { VideoModel } from "@ziggapp/klap-core/video";
import { SocialProviderAccount } from "../hooks/useSocialProviderAccounts";
import { PublicationModel, SocialProviderType } from "../types/models";
import {
  FacebookPostPayload,
  InstagramPostPayload,
  LinkedInPostPayload,
  SocialPostPayload,
  TiktokPostPayload,
  YouTubePostPayload,
} from "../types/publications";

/** A post that is part of a folder's plan: live, in flight, or booked. A
 *  short holding one is COMMITTED: the schedule dialog anchors it, the
 *  folder's "Schedule posts" leaves it unselected, and the session hook
 *  opens it in edit mode. Drafts and failed posts are not done work. */
export const COMMITTED_PUBLICATION_STATUSES = new Set<
  PublicationModel["status"]
>(["scheduled", "published", "publishing", "verifying"]);

/**
 * A helper class for publication-related operations
 */
export class PublicationHelper {
  /**
   * Gets the appropriate title field from a provider's payload
   * @param provider The social provider type
   * @param payload The payload object for the social post
   * @returns The title field value from the provider-specific payload
   */
  static getTitleFromProvider(
    provider: SocialProviderType,
    payload: SocialPostPayload,
  ): string | undefined {
    if (provider === "youtube") return (payload as YouTubePostPayload).title;

    if (provider === "tiktok") return (payload as TiktokPostPayload).title;

    if (provider === "instagram")
      return (payload as InstagramPostPayload).caption;

    if (provider === "linkedin")
      return (payload as LinkedInPostPayload).shareCommentary;

    if (provider === "facebook") return (payload as FacebookPostPayload).caption;
  }

  /**
   * Gets publications that have matching accounts from the selected account IDs
   * @param publications Array of publication models
   * @param accounts Array of social provider accounts
   * @param selectedAccountIds Array of selected account IDs
   * @returns Array of publication and account pairs
   */
  static getPublicationsToDisplayWithAccount(
    publications: PublicationModel[] | undefined,
    accounts: SocialProviderAccount[] | undefined,
    selectedAccountIds: string[],
  ): { publication: PublicationModel; account: SocialProviderAccount }[] {
    if (!publications || !accounts || !selectedAccountIds.length) {
      return [];
    }

    return publications
      .filter((publication) =>
        selectedAccountIds.includes(publication.accountId),
      )
      .map((publication) => {
        const account = accounts.find(
          (account) => account.id === publication.accountId,
        );
        if (!account) return null;

        return { publication, account };
      })
      .filter(
        (
          item,
        ): item is {
          publication: PublicationModel;
          account: SocialProviderAccount;
        } => item !== null,
      );
  }

  /**
   * Video ids that should enter the bulk scheduler in edit mode (scheduled or published posts).
   * Aligns with SelectVideoPublicationForm and useSchedulingSession rules.
   */
  static getInitialVideoIdsWithCommittedPublications(
    videoId: string,
    publications: PublicationModel[] | undefined,
  ): string[] | undefined {
    const committed = (publications ?? []).some(
      (p) =>
        p.projectId === videoId &&
        (p.status === "scheduled" || p.status === "published"),
    );
    return committed ? [videoId] : undefined;
  }

  /**
   * The shorts (`projectId`) holding at least one committed post
   * (COMMITTED_PUBLICATION_STATUSES). Posts without a short are ignored.
   */
  static getCommittedVideoIds(
    publications: PublicationModel[] | undefined,
  ): Set<string> {
    const videoIds = new Set<string>();
    for (const publication of publications ?? []) {
      if (!publication.projectId) continue;
      if (!COMMITTED_PUBLICATION_STATUSES.has(publication.status)) continue;
      videoIds.add(publication.projectId);
    }
    return videoIds;
  }

  /**
   * The door rule between the two schedule surfaces: a short holding ANY
   * persisted post — booked, in flight, live, failed, even a stray draft —
   * is managed on /calendar (Manage); a short holding none is composed in
   * the schedule dialog (Compose). Posts without a short are ignored.
   */
  static hasAnyPost(
    videoId: string,
    publications: PublicationModel[] | undefined,
  ): boolean {
    return (publications ?? []).some((p) => p.projectId === videoId);
  }

  /** The shorts (`projectId`) holding at least one persisted post. */
  static getVideoIdsWithAnyPost(
    publications: PublicationModel[] | undefined,
  ): Set<string> {
    const videoIds = new Set<string>();
    for (const publication of publications ?? []) {
      if (publication.projectId) videoIds.add(publication.projectId);
    }
    return videoIds;
  }

  /**
   * What the folder's "Schedule posts" preselects: the shorts that still
   * need a plan. Committed shorts are never candidates — the select step
   * shows them locked, and they ride into the dialog as context rows
   * (§16.1) — and among the rest the liked shorts win when there are any,
   * else all of them. A folder whose every short is committed selects
   * nothing: the step's footer button then opens the plan (read face)
   * instead of scheduling.
   */
  static getDefaultScheduleSelection(
    videos: VideoModel[],
    committedVideoIds: ReadonlySet<string>,
  ): string[] {
    const uncommitted = videos.filter(
      (video) => !committedVideoIds.has(video.id),
    );
    const liked = uncommitted.filter((video) => Boolean(video.likedByAuthor));
    return (liked.length > 0 ? liked : uncommitted).map((video) => video.id);
  }

  /**
   * Creates default payload objects for different social provider types
   * @param provider The social provider type
   * @returns A default payload object with empty/default values
   */
  static getDefaultPayloadForProvider(
    provider: string,
    publicationTitle: string | null,
  ): SocialPostPayload {
    switch (provider) {
      case "youtube":
        return {
          title: publicationTitle ?? "",
          description: "",
          tags: [],
          visibility: "public",
          categoryId: "22",
        } as YouTubePostPayload;
      case "tiktok":
        return {
          title: publicationTitle ?? "",
          privacyLevel: "PUBLIC_TO_EVERYONE",
          disableDuet: false,
          disableStitch: false,
          disableComment: false,
          videoCoverTimestampMs: 0,
          brandContentToggle: false,
          brandOrganicToggle: false,
          isAigc: false,
        } as TiktokPostPayload;
      case "instagram":
        return {
          caption: publicationTitle ?? "",
          shareToFeed: true,
        } as InstagramPostPayload;
      case "facebook":
        return {
          caption: publicationTitle ?? "",
        } as FacebookPostPayload;
      case "linkedin":
        return {
          shareCommentary: publicationTitle ?? "",
          mediaTitle: "",
          mediaDescription: "",
          visibility: "PUBLIC",
        } as LinkedInPostPayload;
      default:
        // Fallback to a basic YouTube payload if provider is unknown
        return {
          title: publicationTitle ?? "",
          description: "",
          tags: [],
          visibility: "public",
          categoryId: "22",
        } as YouTubePostPayload;
    }
  }

  /**
   * Creates a synthetic publication for a provider account that doesn't have an associated publication yet
   * @param accountId The ID of the provider account
   * @param provider The provider type (e.g., "youtube", "tiktok")
   * @param ownerId The ID of the owner
   * @returns A synthetic PublicationModel object
   */
  static createEmptyPublicationForProvider(
    accountId: string,
    provider: SocialProviderType,
    ownerId: string,
    publicationCaption: string | null,
  ): PublicationModel {
    return {
      id: crypto.randomUUID(),
      createdAt: new Date(),
      scheduledAt: new Date(),
      autoFolderId: null,
      postInfo: PublicationHelper.getDefaultPayloadForProvider(
        provider,
        publicationCaption,
      ),
      status: "draft",
      ownerId,
      accountId,
      provider,
      videoUrl: null,
      projectId: null,
      publishedUrl: null,
      exportId: null,
      errorCode: null,
    };
  }

  /**
   * Gets the appropriate update object for a title update based on the provider type
   * @param provider The social provider type
   * @param title The new title value
   * @returns An object with the appropriate field updated based on provider
   */
  static getPublicationTitleUpdateByProvider(
    provider: SocialProviderType,
    title: string,
  ): Partial<SocialPostPayload> {
    switch (provider) {
      case "youtube":
        return { title };
      case "tiktok":
        return { title };
      case "instagram":
        return { caption: title };
      case "linkedin":
        return { shareCommentary: title };
      case "facebook":
        return { caption: title };
    }
  }

  /**
   * The short's ONE AI caption — platform-agnostic, meant to be written to
   * every post of the short (there is no per-provider caption: one
   * generation per short, not one per platform).
   * @param projectId The short (project) id
   * @param options Tone, optional free-form instructions and, with a
   *   prompt, the current caption to rewrite instead of starting over
   * @param accessToken The access token for authentication
   */
  static async generateCaption(
    projectId: string,
    options: { tone: string; prompt?: string; currentCaption?: string },
    accessToken: string,
  ): Promise<string> {
    const params = new URLSearchParams({ tone: options.tone });
    if (options.prompt) params.set("prompt", options.prompt);
    if (options.currentCaption)
      params.set("current_caption", options.currentCaption);

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/projects/${projectId}/caption?${params.toString()}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    if (!response.ok) {
      throw new Error("Failed to generate caption");
    }
    const data = (await response.json()) as { caption: string };
    return data.caption;
  }

  /**
   * Posts a video to a social media platform
   * @param account The social provider account
   * @param payload The provider-specific payload
   * @param projectId The ID of the project
   * @param accessToken The access token for authentication
   * @param scheduledAt Optional scheduled date
   * @returns The response from the API
   */
  static async publishVideo(
    account: SocialProviderAccount,
    payload: SocialPostPayload,
    projectId: string,
    accessToken: string,
    scheduledAt?: Date,
  ) {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/integrations/providers/${account.provider}/accounts/${account.id}/publications`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            project_id: projectId,
            post_info: PublicationHelper.camelToSnakeCase(payload),
            scheduled_at: scheduledAt?.toISOString() ?? undefined,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to post");
      }

      return await response.json();
    } catch (error) {
      console.error(error);
      throw new Error("Failed to post");
    }
  }

  /**
   * Helper function to convert camelCase to snake_case
   * This is usually imported from api.ts but included here for completeness
   */
  private static camelToSnakeCase(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (typeof obj !== "object") {
      return obj;
    }

    // Handle Date objects by returning them as-is
    if (obj instanceof Date) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(PublicationHelper.camelToSnakeCase);
    }

    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key.replace(/([A-Z])/g, "_$1").toLowerCase(),
        PublicationHelper.camelToSnakeCase(value),
      ]),
    );
  }

  static updatePayloadTitle(
    payload: SocialPostPayload,
    provider: SocialProviderType,
    title: string,
  ): SocialPostPayload {
    const titleUpdate = this.getPublicationTitleUpdateByProvider(
      provider,
      title,
    );

    return { ...payload, ...titleUpdate } as SocialPostPayload;
  }

  static updatePayloadVisibility(
    payload: SocialPostPayload,
    provider: SocialProviderType,
    visibility: string,
  ): SocialPostPayload {
    switch (provider) {
      case "youtube":
        return { ...payload, visibility } as YouTubePostPayload;
      case "tiktok":
        if (visibility === "SELF_ONLY") {
          return {
            ...payload,
            privacyLevel: visibility,
            brandContentToggle: false,
            brandOrganicToggle: false,
          } as TiktokPostPayload;
        }
        return { ...payload, privacyLevel: visibility } as TiktokPostPayload;
      case "linkedin":
        return { ...payload, visibility } as LinkedInPostPayload;
      case "instagram":
        return {
          ...payload,
          shareToFeed: visibility === "public",
        } as InstagramPostPayload;
      default:
        return payload;
    }
  }

  // Function to convert payload from one provider type to another
  static convertPayloadBetweenProviders(
    payload: SocialPostPayload,
    fromProvider: SocialProviderType,
    toProvider: SocialProviderType,
  ): SocialPostPayload {
    // Extract content from the source payload
    const title = this.getTitleFromProvider(fromProvider, payload);

    // Create a default payload for the target provider
    return this.getDefaultPayloadForProvider(toProvider, title ?? null);
  }
}
